import React, { createContext, useContext, useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from './AuthContext';
import { paymentService } from '../services/api';

interface StripeContextType {
  loading: boolean;
  credits: number;
  createCheckoutSession: (priceId: string) => Promise<void>;
  checkCredits: () => Promise<void>;
}

const StripeContext = createContext<StripeContextType | null>(null);

export function useStripe() {
  const context = useContext(StripeContext);
  if (!context) {
    throw new Error('useStripe must be used within a StripeProvider');
  }
  return context;
}

// Get Stripe public key from environment variables
const STRIPE_PUBLIC_KEY = "pk_live_51RHnNeL7dru6VkDzkkdBNS1SCtvKfFvi3EGjyPrz28UCNR6PMDcvNdKDqiz0XaQx6hps4JAO8Ezlag9SvOd4c9Zy00PcPUT6Dm";

// Log the Stripe public key for debugging
// console.log('Stripe Public Key:', STRIPE_PUBLIC_KEY ? `${STRIPE_PUBLIC_KEY.substring(0, 8)}...` : 'Not set');

// Load Stripe only if the public key is available
const stripePromise = STRIPE_PUBLIC_KEY ? loadStripe(STRIPE_PUBLIC_KEY) : null;

export function StripeProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(false);
  const [credits, setCredits] = useState(0);
  const { user } = useAuth();

  // Fetch credits when the component mounts and when user changes
  useEffect(() => {
    if (user) {
      checkCredits();
    }
  }, [user, checkCredits]);

  async function createCheckoutSession(priceId: string) {
    if (!user) {
      throw new Error('Must be logged in to purchase credits');
    }

    if (!STRIPE_PUBLIC_KEY) {
      throw new Error('Stripe public key is not configured. Please check your environment variables.');
    }

    if (!stripePromise) {
      throw new Error('Stripe failed to initialize. Please check your Stripe configuration.');
    }

    setLoading(true);
    try {
      // console.log('Creating checkout session with price ID:', priceId);
      const response = await paymentService.createCheckoutSession(priceId);
      // console.log('Checkout session created:', response);

      const { sessionId } = response;
      const stripe = await stripePromise;
      
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      const { error } = await stripe.redirectToCheckout({ sessionId });
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  async function checkCredits() {
    if (!user) return;

    try {
      const response = await paymentService.getUserCredits();
      setCredits(response.credits);
    } catch (error) {
      console.error('Error checking credits:', error);
    }
  }

  const value = {
    loading,
    credits,
    createCheckoutSession,
    checkCredits
  };

  return (
    <StripeContext.Provider value={value}>
      {children}
    </StripeContext.Provider>
  );
}
